import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, User, Building2, Sparkles, Mail, Lock, ArrowRight } from 'lucide-react';
import { useSignup } from '../../hooks/useSignup';
import PasswordRequirements from './PasswordRequirements';
import type { ReferralSignupOffer } from '../../lib/referralPartnerOffer';

interface SignupFormProps {
  /** When set, shows a "referred by X / discount" badge in the form header. */
  referralOffer?: ReferralSignupOffer | null;
  /** Pre-fill the account email (e.g. captured by the extended flow's email screen). */
  initialEmail?: string;
  /** Pre-fill password + confirm (e.g. Signup-nexus landing). */
  initialPassword?: string;
  /** Email already verified by the extended flow's OTP screens — locked in, no re-verification. */
  initialVerifiedEmail?: string;
  /**
   * After mount, immediately run step-1 → send OTP (used when the parent
   * landing page already collected email + password).
   */
  autoStartVerification?: boolean;
}

/**
 * The glassmorphism account+business signup card. Owns no creation logic of its
 * own — that lives in useSignup — so it can be embedded in both the standard
 * signup page and the referral landing page with identical behavior.
 */
export default function SignupForm({
  referralOffer,
  initialEmail,
  initialPassword,
  initialVerifiedEmail,
  autoStartVerification = false,
}: SignupFormProps) {
  const {
    formData,
    setFormData,
    validationErrors,
    currentStep,
    loading,
    error,
    showPassword,
    setShowPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    handleNextStep,
    handlePrevStep,
    handleSubmit,
    awaitingVerification,
    code,
    setCode,
    codeError,
    sendingCode,
    verifying,
    emailInUse,
    handleVerifyCode,
    handleResendCode,
    handleBackFromVerify,
  } = useSignup({ initialEmail, initialPassword, initialVerifiedEmail });

  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (!autoStartVerification || autoStartedRef.current) return;
    autoStartedRef.current = true;
    void handleNextStep();
    // Intentionally once on mount — credentials are already seeded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStartVerification]);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const showDiscount = !!(referralOffer?.signup_discount_enabled && referralOffer?.signup_discount_percent);

  return (
    <div className="w-full max-w-md">
      <div className="bg-white rounded-t-[20px] sm:rounded-[24px] shadow-[0_2px_16px_rgba(0,0,0,0.06)] overflow-hidden border border-gray-200">
        {/* Header */}
        <div className="bg-gray-50/60 border-b border-gray-100 px-6 py-6 text-center">
          <h1 className="text-2xl font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
            Create Account
          </h1>
          <p className="text-gray-500 text-sm mt-1">Join Nexus to start managing your business</p>

          {referralOffer?.partner_name && (
            <div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1.5 text-[12px] font-semibold text-emerald-700">
              <Sparkles className="h-3.5 w-3.5" />
              Referred by {referralOffer.partner_name}
              {showDiscount ? ` · ${referralOffer.signup_discount_percent}% off your first month` : ''}
            </div>
          )}

          {/* Progress Indicator */}
          <div className="flex items-center justify-center space-x-4 mt-6">
            <div className={`flex items-center ${currentStep >= 1 ? 'text-gray-900' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${currentStep >= 1 ? 'border-gray-900 bg-gray-900/[0.04]' : 'border-gray-300'}`}>
                <span className="text-sm font-semibold">1</span>
              </div>
              <span className="ml-2 text-sm font-medium hidden sm:inline">Account</span>
            </div>
            <div className={`w-8 h-0.5 ${currentStep >= 2 ? 'bg-gray-900' : 'bg-gray-200'}`}></div>
            <div className={`flex items-center ${currentStep >= 2 ? 'text-gray-900' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${currentStep >= 2 ? 'border-gray-900 bg-gray-900/[0.04]' : 'border-gray-300'}`}>
                <span className="text-sm font-semibold">2</span>
              </div>
              <span className="ml-2 text-sm font-medium hidden sm:inline">Business</span>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div
          className="flex-1 overflow-y-auto"
          style={{ WebkitOverflowScrolling: 'touch', paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0' }}
        >
          <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-5`}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
                {error}
              </div>
            )}

            {autoStartVerification && sendingCode && !awaitingVerification && currentStep === 1 ? (
              <div className="py-10 text-center space-y-3">
                <div className="mx-auto w-8 h-8 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
                <p className="text-sm text-gray-500">Sending verification code…</p>
              </div>
            ) : awaitingVerification ? (
              /* Email verification step */
              <div className="space-y-5 text-center">
                <div className="mx-auto w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                  <Mail className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-gray-900 text-lg font-semibold">Verify your email</h3>
                  <p className="text-sm text-gray-400 mt-1">
                    We sent a 6-digit code to <span className="text-gray-900 font-medium">{formData.email}</span>
                  </p>
                </div>
                <div>
                  <input
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleVerifyCode(); }}
                    placeholder="••••••"
                    className="w-full text-center text-gray-900 placeholder-gray-300 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:ring-0 focus:border-gray-400 transition-all touch-manipulation"
                    style={{ fontSize: '26px', letterSpacing: '0.4em', minHeight: '56px', WebkitTapHighlightColor: 'transparent' }}
                    autoFocus
                  />
                  {codeError && <p className="mt-2 text-sm text-red-600">{codeError}</p>}
                </div>
                <button
                  type="button"
                  onClick={handleVerifyCode}
                  disabled={verifying || code.length !== 6}
                  className={`w-full ${isMobile ? 'px-4 py-3 text-[15px]' : 'px-6 py-3 text-base'} font-semibold text-white rounded-full transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm bg-gray-900 hover:bg-black active:bg-black disabled:opacity-50 disabled:cursor-not-allowed`}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  {verifying ? (
                    <span className="flex items-center justify-center">
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3" />
                      Verifying...
                    </span>
                  ) : (
                    'Verify & Continue'
                  )}
                </button>
                <div className="text-sm text-gray-500">
                  Didn't get it?{' '}
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={sendingCode}
                    className="font-semibold text-gray-900 underline underline-offset-2 hover:text-gray-600 disabled:opacity-50"
                  >
                    {sendingCode ? 'Sending...' : 'Resend code'}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={handleBackFromVerify}
                  className="text-sm text-gray-400 hover:text-gray-600"
                >
                  ← Use a different email
                </button>
              </div>
            ) : currentStep === 1 ? (
              <>
                <div className="space-y-4">
                  <h3 className="text-gray-900 text-lg font-semibold flex items-center">
                    <User className="h-5 w-5 mr-2 text-gray-400" />
                    Account Details
                  </h3>

                  <div>
                    <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-2">Email</label>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      value={formData.email}
                      onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                      className={`w-full ${isMobile ? 'px-3 py-2.5' : 'px-4 py-3'} text-base text-gray-900 placeholder-gray-400 bg-white border ${validationErrors.email ? 'border-red-300' : 'border-gray-200'} rounded-2xl focus:outline-none focus:ring-0 focus:border-gray-400 transition-all touch-manipulation`}
                      placeholder="Enter your email"
                      style={{ fontSize: '16px', minHeight: isMobile ? '44px' : '48px', WebkitTapHighlightColor: 'transparent' }}
                    />
                    {validationErrors.email && (
                      <p className="mt-1.5 text-sm text-red-600">
                        {validationErrors.email}
                        {emailInUse && (
                          <>
                            {' · '}
                            <Link to="/login" className="font-semibold text-gray-900 underline underline-offset-2 hover:text-gray-600">Sign in</Link>
                          </>
                        )}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-xs font-bold text-gray-700 mb-2">Password</label>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        value={formData.password}
                        onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                        className={`w-full ${isMobile ? 'px-3 py-2.5 pr-12' : 'px-4 py-3 pr-12'} text-base text-gray-900 placeholder-gray-400 bg-white border ${validationErrors.password ? 'border-red-300' : 'border-gray-200'} rounded-2xl focus:outline-none focus:ring-0 focus:border-gray-400 transition-all touch-manipulation`}
                        placeholder="••••••••"
                        style={{ fontSize: '16px', minHeight: isMobile ? '44px' : '48px', WebkitTapHighlightColor: 'transparent' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors rounded-full p-1 hover:bg-gray-100 active:bg-gray-200 active:scale-95"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                    {validationErrors.password && <p className="mt-1.5 text-sm text-red-600">{validationErrors.password}</p>}
                    <div className="mt-2.5">
                      <PasswordRequirements password={formData.password} variant="light" />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-xs font-bold text-gray-700 mb-2">Confirm Password</label>
                    <div className="relative">
                      <input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                        className={`w-full ${isMobile ? 'px-3 py-2.5 pr-12' : 'px-4 py-3 pr-12'} text-base text-gray-900 placeholder-gray-400 bg-white border ${validationErrors.confirmPassword ? 'border-red-300' : 'border-gray-200'} rounded-2xl focus:outline-none focus:ring-0 focus:border-gray-400 transition-all touch-manipulation`}
                        placeholder="••••••••"
                        style={{ fontSize: '16px', minHeight: isMobile ? '44px' : '48px', WebkitTapHighlightColor: 'transparent' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors rounded-full p-1 hover:bg-gray-100 active:bg-gray-200 active:scale-95"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                    {validationErrors.confirmPassword && <p className="mt-1.5 text-sm text-red-600">{validationErrors.confirmPassword}</p>}
                  </div>
                </div>

                <div className={`${isMobile ? 'pt-2' : 'pt-3'}`}>
                  <button
                    type="button"
                    onClick={handleNextStep}
                    disabled={sendingCode}
                    className={`w-full ${isMobile ? 'px-4 py-3 text-[15px]' : 'px-6 py-3 text-base'} font-semibold text-white rounded-full transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm bg-gray-900 hover:bg-black active:bg-black disabled:opacity-50 disabled:cursor-not-allowed`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {sendingCode ? (
                      <span className="flex items-center justify-center">
                        <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3" />
                        Sending code...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        Continue <ArrowRight className="h-5 w-5 text-emerald-300" />
                      </span>
                    )}
                  </button>
                </div>

                <div className="text-center pt-2">
                  <p className="text-sm text-gray-500">
                    Already have an account?{' '}
                    <Link to="/login" className="font-semibold text-gray-900 underline underline-offset-2 hover:text-gray-600 transition-colors" style={{ WebkitTapHighlightColor: 'transparent' }}>
                      Sign in
                    </Link>
                  </p>
                  <p className="mt-4 text-[12px] leading-relaxed text-gray-400">
                    By clicking Continue, you agree to our{' '}
                    <a
                      href="https://www.nexusbookai.com/terms-of-service"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-2 text-gray-500 hover:text-gray-700"
                    >
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a
                      href="https://www.nexusbookai.com/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-2 text-gray-500 hover:text-gray-700"
                    >
                      Privacy Policy
                    </a>
                    .
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-4">
                  <h3 className="text-gray-900 text-lg font-semibold flex items-center">
                    <User className="h-5 w-5 mr-2 text-gray-400" />
                    Your Details
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-2">First Name</label>
                      <input
                        id="firstName"
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
                        className={`w-full ${isMobile ? 'px-3 py-2.5' : 'px-4 py-3'} text-base text-gray-900 placeholder-gray-400 bg-white border ${validationErrors.firstName ? 'border-red-300' : 'border-gray-200'} rounded-2xl focus:outline-none focus:ring-0 focus:border-gray-400 transition-all touch-manipulation`}
                        placeholder="First name"
                        style={{ fontSize: '16px', minHeight: isMobile ? '44px' : '48px', WebkitTapHighlightColor: 'transparent' }}
                      />
                      {validationErrors.firstName && <p className="mt-1.5 text-sm text-red-600">{validationErrors.firstName}</p>}
                    </div>

                    <div>
                      <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-2">Last Name</label>
                      <input
                        id="lastName"
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))}
                        className={`w-full ${isMobile ? 'px-3 py-2.5' : 'px-4 py-3'} text-base text-gray-900 placeholder-gray-400 bg-white border ${validationErrors.lastName ? 'border-red-300' : 'border-gray-200'} rounded-2xl focus:outline-none focus:ring-0 focus:border-gray-400 transition-all touch-manipulation`}
                        placeholder="Last name"
                        style={{ fontSize: '16px', minHeight: isMobile ? '44px' : '48px', WebkitTapHighlightColor: 'transparent' }}
                      />
                      {validationErrors.lastName && <p className="mt-1.5 text-sm text-red-600">{validationErrors.lastName}</p>}
                    </div>
                  </div>

                  <h3 className="text-gray-900 text-lg font-semibold flex items-center pt-2">
                    <Building2 className="h-5 w-5 mr-2 text-gray-400" />
                    Business Information
                  </h3>

                  <div>
                    <label htmlFor="businessName" className="block text-xs font-bold text-gray-700 mb-2">Business Name</label>
                    <input
                      id="businessName"
                      type="text"
                      value={formData.businessName}
                      onChange={(e) => setFormData((prev) => ({ ...prev, businessName: e.target.value }))}
                      className={`w-full ${isMobile ? 'px-3 py-2.5' : 'px-4 py-3'} text-base text-gray-900 placeholder-gray-400 bg-white border ${validationErrors.businessName ? 'border-red-300' : 'border-gray-200'} rounded-2xl focus:outline-none focus:ring-0 focus:border-gray-400 transition-all touch-manipulation`}
                      placeholder="Enter your business name"
                      style={{ fontSize: '16px', minHeight: isMobile ? '44px' : '48px', WebkitTapHighlightColor: 'transparent' }}
                    />
                    {validationErrors.businessName && <p className="mt-1.5 text-sm text-red-600">{validationErrors.businessName}</p>}
                  </div>

                  <div>
                    <label htmlFor="businessEmail" className="block text-xs font-bold text-gray-700 mb-2">Email</label>
                    <div className="relative">
                      <input
                        id="businessEmail"
                        type="email"
                        value={formData.businessEmail}
                        disabled
                        readOnly
                        className={`w-full ${isMobile ? 'px-3 py-2.5 pr-11' : 'px-4 py-3 pr-11'} text-base text-gray-500 bg-gray-50 border border-gray-200 rounded-2xl cursor-not-allowed`}
                        style={{ fontSize: '16px', minHeight: isMobile ? '44px' : '48px' }}
                      />
                      <Lock className="absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" aria-hidden />
                    </div>
                    <p className="mt-1.5 text-xs text-gray-500">Verified on the previous step.</p>
                  </div>

                  <div>
                    <label htmlFor="businessPhone" className="block text-xs font-bold text-gray-700 mb-2">Business Phone</label>
                    <input
                      id="businessPhone"
                      type="tel"
                      value={formData.businessPhone}
                      onChange={(e) => setFormData((prev) => ({ ...prev, businessPhone: e.target.value }))}
                      className={`w-full ${isMobile ? 'px-3 py-2.5' : 'px-4 py-3'} text-base text-gray-900 placeholder-gray-400 bg-white border ${validationErrors.businessPhone ? 'border-red-300' : 'border-gray-200'} rounded-2xl focus:outline-none focus:ring-0 focus:border-gray-400 transition-all touch-manipulation`}
                      placeholder="(555) 123-4567"
                      style={{ fontSize: '16px', minHeight: isMobile ? '44px' : '48px', WebkitTapHighlightColor: 'transparent' }}
                    />
                    {validationErrors.businessPhone && <p className="mt-1.5 text-sm text-red-600">{validationErrors.businessPhone}</p>}
                  </div>

                  <div>
                    <label htmlFor="businessAddress" className="block text-xs font-bold text-gray-700 mb-2">
                      Business Address <span className="text-gray-500 font-normal">(Optional)</span>
                    </label>
                    <input
                      id="businessAddress"
                      type="text"
                      value={formData.businessAddress}
                      onChange={(e) => setFormData((prev) => ({ ...prev, businessAddress: e.target.value }))}
                      className={`w-full ${isMobile ? 'px-3 py-2.5' : 'px-4 py-3'} text-base text-gray-900 placeholder-gray-400 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:ring-0 focus:border-gray-400 transition-all touch-manipulation`}
                      placeholder="123 Main St, City, State"
                      style={{ fontSize: '16px', minHeight: isMobile ? '44px' : '48px', WebkitTapHighlightColor: 'transparent' }}
                    />
                  </div>
                </div>

                <div className={`${isMobile ? 'pt-2' : 'pt-3'} space-y-3`}>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={loading}
                    className={`w-full ${isMobile ? 'px-4 py-3 text-[15px]' : 'px-6 py-3 text-base'} font-semibold text-white rounded-full transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm ${loading ? 'bg-gray-500 cursor-not-allowed' : 'bg-gray-900 hover:bg-black active:bg-black'} disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                        Creating Account...
                      </div>
                    ) : (
                      'Create Account'
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handlePrevStep}
                    className={`w-full ${isMobile ? 'px-4 py-3 text-[15px]' : 'px-6 py-3 text-base'} font-semibold text-gray-600 bg-white border border-gray-200 rounded-full transition-all duration-150 active:scale-[0.98] touch-manipulation hover:bg-gray-50 active:bg-gray-100`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    Back
                  </button>
                </div>

                <div className="text-center pt-2">
                  <p className="text-sm text-gray-500">
                    Already have an account?{' '}
                    <Link to="/login" className="font-semibold text-gray-900 underline underline-offset-2 hover:text-gray-600 transition-colors" style={{ WebkitTapHighlightColor: 'transparent' }}>
                      Sign in
                    </Link>
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
